/**
 * DOCUMENTATION:
 * Component: SystemDesignMicroservices
 * Purpose: Teaching the transition from Monolith to Microservices and the role of API Gateways.
 * * * Note for Junior Developers:
 * - Monolith: All features (Auth, Billing, Search) live in one single application.
 * - Microservices: Each feature is a separate, independent service with its own database.
 * - API Gateway: A single entry point for the frontend that routes requests to the
 * correct microservice, handles authentication, and performs rate limiting.
 * - Loose Coupling: Services should be able to change or deploy without affecting others.
 */

import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
} from "@mui/material";

/* Icons */
import HubIcon from '@mui/icons-material/Hub';
import LayersIcon from '@mui/icons-material/Layers';
import SecurityIcon from '@mui/icons-material/Security';
import RouterIcon from '@mui/icons-material/Router';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "white",
}));

const CompareBox = styled(Box)(({ theme, isMicro }) => ({
    padding: theme.spacing(3),
    borderRadius: "12px",
    backgroundColor: isMicro ? "rgba(0, 200, 255, 0.05)" : "rgba(255, 255, 255, 0.03)",
    border: `1px solid ${isMicro ? "#00c8ff" : "rgba(255, 255, 255, 0.1)"}`,
    height: "100%",
}));

export default function SystemDesignMicroservices() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    MICROSERVICES & GATEWAYS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 1. Monolith vs Microservices */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#00c8ff" }}><LayersIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Breaking the Monolith</Typography>
                        </Box>
                        <Typography mb={4} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            In a <strong>Monolith</strong>, everything is packaged together. In <strong>Microservices</strong>,
                            you have a suite of small services, each running in its own process and managing its own data.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6}>
                                <CompareBox isMicro={false}>
                                    <Typography variant="h6" fontWeight={700} gutterBottom>The Monolith</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        Simple to develop and deploy initially. However, as it grows, the codebase becomes
                                        intertwined, making it hard to scale specific parts or adopt new technologies.
                                    </Typography>
                                </CompareBox>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CompareBox isMicro={true}>
                                    <Typography variant="h6" fontWeight={700} color="#00c8ff" gutterBottom>Microservices</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        Scales independently. Different teams can work on different services using different
                                        languages. If the 'Recommendation Service' crashes, the 'Checkout Service' stays up.
                                    </Typography>
                                </CompareBox>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. The API Gateway */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#4ade80" }}><RouterIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>The API Gateway Pattern</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            You don't want your React app to have to know the URLs of 50 different microservices.
                            The <strong>API Gateway</strong> acts as a "Reverse Proxy"—it provides a single URL for the
                            frontend and routes requests to the correct internal service.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 4 }}>
                            <Grid item xs={12} md={4}>
                                <Stack alignItems="center" textAlign="center">
                                    <SecurityIcon sx={{ color: "#00c8ff", fontSize: 40, mb: 1 }} />
                                    <Typography fontWeight={700}>Authentication</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.6 }}>Verify tokens in one place before hitting services.</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Stack alignItems="center" textAlign="center">
                                    <HubIcon sx={{ color: "#00c8ff", fontSize: 40, mb: 1 }} />
                                    <Typography fontWeight={700}>Request Routing</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.6 }}>Maps /users to the User Service and /orders to the Order Service.</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Stack alignItems="center" textAlign="center">
                                    <Avatar sx={{ bgcolor: "transparent", border: "2px solid #00c8ff", color: "#00c8ff", width: 40, height: 40, mb: 1 }}>RL</Avatar>
                                    <Typography fontWeight={700}>Rate Limiting</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.6 }}>Protect your internal services from being flooded by a single user.</Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
